import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { supportRequestService } from "@/app/services/supportRequestService";
import { ListMySupportRequestsResponse } from "@/app/services/supportRequestService/listAllSupport";
import {
  SUPPORT_REASON_LABEL,
  SUPPORT_STATUS_BADGE_CLASSNAME,
  SUPPORT_STATUS_LABEL,
  SupportRequest as SupportRequestType,
  SupportRequestReason,
  SupportRequestStatusEnum,
} from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import { cn } from "@/app/utils/cn";
import {
  allowedTransitions,
  ownerAllowedStatus,
  updateNotAllowed,
  validateSupportFiles,
} from "@/app/utils/supportRequest";
import { buildChatItems } from "@/app/utils/supportChat";
import { LinkifiedText } from "@/view/components/LinkifiedText";
import { Badge } from "@/view/components/ui/badge";
import { useToast } from "@/view/components/ui/use-toast";

export interface DetailField {
  label: string;
  content: ReactNode;
  type?: "default" | "row";
}

interface UseSupportRequestDetailSheetParams {
  visible: boolean;
  number: string | null;
  onClose?: () => void;
}

const STATUS_UPDATED_TITLE: Partial<Record<SupportRequestStatusEnum, string>> =
  {
    [SupportRequestStatusEnum.RESOLVED]: "Chamado resolvido",
    [SupportRequestStatusEnum.CLOSED]: "Chamado encerrado",
    [SupportRequestStatusEnum.CANCELLED]: "Chamado cancelado",
  };

const formatDate = (value: string | Date) =>
  format(new Date(value), "dd/MM/yyyy", { locale: ptBR });

export const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const sellerFields = (supportRequest: SupportRequestType): DetailField[] => {
  return [
    {
      label: "Nome",
      content:
        supportRequest.seller?.name ?? supportRequest.metadata?.contact.name,
    },
    {
      label: "Email",
      content:
        supportRequest.seller?.email ?? supportRequest.metadata?.contact.email,
    },
    {
      label: "Telefone",
      content:
        supportRequest.seller?.phone ?? supportRequest.metadata?.contact.phone,
    },
  ];
};
const getDetailFields = (
  supportRequest: SupportRequestType,
  isSupportStaff: boolean,
): DetailField[] => {
  const status = supportRequest.status as SupportRequestStatusEnum;
  const reason = supportRequest.reason as SupportRequestReason;

  return [
    {
      label: "Status",
      content: (
        <Badge
          className={cn(
            "w-fit whitespace-nowrap border-transparent font-semibold",
            SUPPORT_STATUS_BADGE_CLASSNAME[status],
          )}
        >
          {SUPPORT_STATUS_LABEL[status]}
        </Badge>
      ),
    },
    {
      label: "Código",
      content: `#${supportRequest.number || "-"}`,
    },
    supportRequest.assignedTo?.name && {
      label: "Responsável",
      content: isSupportStaff ? supportRequest.assignedTo.name : "Suporte",
    },

    {
      label: "Motivo",
      content: SUPPORT_REASON_LABEL[reason],
    },
    {
      label: "Data de abertura",
      content: formatDate(supportRequest.createdAt),
    },
    {
      label: "Última atualização",
      content: formatDate(supportRequest.updatedAt),
    },
    supportRequest.resolvedAt && {
      label: "Resolvido em",
      content: formatDate(supportRequest.resolvedAt),
    },
    {
      type: "row" as const,
      label: "Descrição",
      content: (
        <p className="w-full">
          <LinkifiedText
            text={supportRequest.description?.trim() || "Sem descrição"}
          />
        </p>
      ),
    },
  ].filter(Boolean) as DetailField[];
};

export function useSupportRequestDetailSheet({
  visible,
  number,
  onClose,
}: UseSupportRequestDetailSheetParams) {
  const { user, isSuperAdmin, isAdmin, isSupport } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const { data: supportRequest, isLoading } = useQuery({
    queryKey: ["supportRequestDetail", number],
    queryFn: () => supportRequestService.getDetailByNumber(number!),
    enabled: !!number && visible,
  });

  useEffect(() => {
    if (!supportRequest) return;
    queryClient.setQueriesData<InfiniteData<ListMySupportRequestsResponse>>(
      { queryKey: ["mySupportRequests"] },
      (current) =>
        current && {
          ...current,
          pages: current.pages.map((page) => ({
            ...page,
            supportRequests: page.supportRequests.map((request) =>
              request._id === supportRequest._id
                ? { ...request, hasUnreadMessage: false }
                : request,
            ),
          })),
        },
    );
  }, [supportRequest, queryClient]);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["mySupportRequests"] });
    queryClient.invalidateQueries({
      queryKey: ["supportRequestDetail", number],
    });
  };

  // Chamado encerrado some da lista na hora, a menos que o filtro ativo
  // peça justamente esse status (aí só atualiza o status na linha).
  const removeFromLists = (to: SupportRequestStatusEnum) => {
    const id = supportRequest?._id;
    if (!id) return;

    queryClient
      .getQueriesData<InfiniteData<ListMySupportRequestsResponse>>({
        queryKey: ["mySupportRequests"],
      })
      .forEach(([key, current]) => {
        if (!current) return;

        const statusFilter = (key[1] as { status?: SupportRequestStatusEnum[] })
          ?.status;
        const keepInList = !!statusFilter?.includes(to);

        queryClient.setQueryData<InfiniteData<ListMySupportRequestsResponse>>(
          key,
          {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              supportRequests: keepInList
                ? page.supportRequests.map((request) =>
                    request._id === id ? { ...request, status: to } : request,
                  )
                : page.supportRequests.filter((request) => request._id !== id),
              pagination: {
                ...page.pagination,
                total: keepInList
                  ? page.pagination.total
                  : Math.max(0, page.pagination.total - 1),
              },
            })),
          },
        );
      });
  };

  const isSupportStaff = isSuperAdmin || isAdmin || isSupport;

  const isFinished =
    !!supportRequest && updateNotAllowed.includes(supportRequest.status);

  const canComment = !!supportRequest && !isFinished;
  const hasMessage = !!message.trim();

  // vendedor e atendente anexam em qualquer mensagem de chamado não encerrado
  const allowAttachments = canComment;

  // o vendedor responde pela rota de reply quando o chamado aguarda a resposta dele
  const useReplyRoute =
    !isSupportStaff &&
    supportRequest?.status === SupportRequestStatusEnum.AWAITING_SELLER;

  const { mutate: sendMessage, isPending: isSending } = useMutation({
    mutationFn: () =>
      useReplyRoute
        ? supportRequestService.replySupportRequest({
            id: supportRequest!._id,
            message,
            files,
          })
        : supportRequestService.updateSupportRequest({
            number: supportRequest!.number!,
            comment: message,
            files,
          }),
    onSuccess: () => {
      setMessage("");
      setFiles([]);
      refetch();
      toast({
        title: "Mensagem enviada com sucesso!",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erro ao enviar mensagem, tente novamente.",
      });
    },
  });

  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useMutation({
      mutationFn: (to?: SupportRequestStatusEnum) =>
        supportRequestService.updateSupportRequest({
          number: supportRequest!.number!,
          status: to,
          comment: message,
          files,
          resolution:
            to === SupportRequestStatusEnum.RESOLVED
              ? message.trim() || undefined
              : undefined,
        }),
      onSuccess: (_data, to) => {
        const isClosing = !!to && updateNotAllowed.includes(to);

        toast({
          title: (to && STATUS_UPDATED_TITLE[to]) || "Chamado atualizado",
        });
        setMessage("");
        setFiles([]);

        if (isClosing) {
          removeFromLists(to);
          onClose?.();
        }

        refetch();
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title:
            error?.response?.data?.message ||
            "Não foi possível atualizar o chamado",
        });
      },
    });

  const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    const { accepted, error } = validateSupportFiles(files, selected);

    if (error) {
      toast({ variant: "destructive", title: error });
    }

    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const canSubmitMessage = (hasMessage || files.length > 0) && !isSending;

  // regra para suporte
  const supportOptions =
    canComment && isSupportStaff
      ? allowedTransitions[supportRequest.status]
      : [];

  const ownerOptions =
    canComment && !isSupportStaff
      ? ownerAllowedStatus.filter((status) => status !== supportRequest.status)
      : [];

  const detailFields = supportRequest
    ? getDetailFields(supportRequest, isSupportStaff)
    : [];
  const infoFields = detailFields.filter((field) => field.type !== "row");
  const textFields = detailFields.filter((field) => field.type === "row");

  // abertura + history em ordem cronológica (mais antiga primeiro)
  const chatItems = useMemo(
    () =>
      supportRequest
        ? buildChatItems({
            supportRequest,
            currentUserId: user?._id,
            isSupportStaff,
          })
        : [],
    [supportRequest, user?._id, isSupportStaff],
  );

  useEffect(() => {
    if (!visible) {
      setIsAttachmentsOpen(false);
      setMessage("");
      setFiles([]);
    }
  }, [visible, number]);

  return {
    user,
    supportRequest,
    isLoading,
    infoFields,
    textFields,
    chatItems,
    isFinished,
    isAttachmentsOpen,
    setIsAttachmentsOpen,
    allowAttachments,
    canSubmitMessage,
    message,
    setMessage,
    files,
    fileInputRef,
    handleSelectFiles,
    handleRemoveFile,
    handleOpenFilePicker,
    sendMessage,
    isSending,
    canComment,
    hasMessage,
    isSupportStaff,
    supportOptions,
    ownerOptions,
    updateStatus,
    isUpdatingStatus,
    sellerFields,
  };
}
